import { message } from "antd";
import { formatDistance } from "date-fns";
import dayjs from "dayjs";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import type { Content, TDocumentDefinitions } from "pdfmake/interfaces";
import {
  DAP_FIELDS,
  NoteType,
  SOAP_FIELDS,
  getCptCodeByPsychotherapyMinutes,
  getCptCodeLabelByPsychotherapyMinutes,
  getCptSuggestedCode,
  getNotesType,
  showAddon,
  showCptCodes,
} from "../domain/notes.domain";
import { PRACTICE_AREA, isNonBehavioralHealthUser } from "../domain/user.domain";
import { UserInfoProps } from "../slices/authSlice";
import { TClientForm, TDiagnosticCode, TNotes, TProceduralCode } from "../types/index.type";
import { EventType, NoteSection, trackEvent } from "./analytics";

// @ts-ignore pdfmake types missing vfs property
(pdfMake as any).vfs = pdfFonts.vfs;

export const convertPlainTextToHtml = (text: string): string => {
  if (!text) {
    return "";
  }

  return text
    .split("\n")
    .map((line: string) => {
      if (line.trim() === "") {
        return "<p><br></p>";
      } else {
        return `<p>${line}</p>`;
      }
    })
    .join("");
};

export function formatDuration(seconds: number) {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  const formattedHours = hrs.toString().padStart(2, "0");
  const formattedMinutes = mins.toString().padStart(2, "0");
  const formattedSeconds = secs.toString().padStart(2, "0");

  return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
}

export function formatTimeToSecondsMinsHoursFromSeconds(seconds: number) {
  if (isNaN(seconds) || !seconds) {
    return "0 sec";
  }
  if (seconds < 60) {
    return `${seconds} sec`;
  } else if (seconds >= 60 && seconds < 3600) {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins} min ${secs} sec`;
  } else {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs} hrs ${mins} min ${secs} sec`;
  }
}

export const showPaymentModal = (userInfo: UserInfoProps) => {
  return !userInfo.is_paying && userInfo.total_recordings >= userInfo.free_recordings_limit;
};

export const copyContent = (
  notesDetails: TNotes,
  htmlContent: string,
  section: NoteSection,
  plainTextContent = "",
): void => {
  if (notesDetails) {
    trackEvent(EventType.COPY_NOTE, {
      noteType: getNotesType(notesDetails.note_type),
      noteSection: section,
      recordingId: notesDetails?.id,
    });
    const copyToClipboard = async (htmlContent: string, plainTextContent = "") => {
      try {
        if (plainTextContent) {
          await navigator.clipboard.write([
            new ClipboardItem({
              "text/html": new Blob([htmlContent], { type: "text/html" }),
              "text/plain": new Blob([plainTextContent], {
                type: "text/plain",
              }),
            }),
          ]);
        } else {
          await navigator.clipboard.write([
            new ClipboardItem({
              "text/html": new Blob([htmlContent], { type: "text/html" }),
              "text/plain": new Blob([htmlContent], { type: "text/plain" }),
            }),
          ]);
        }
        console.log("Content copied to clipboard successfully!");
      } catch (err) {
        console.error("Failed to copy: ", err);
      }
    };

    copyToClipboard(htmlContent, plainTextContent);
  }
};

export const copySectionContent = (notesDetails: TNotes, text: string, section: NoteSection) => {
  if (notesDetails) {
    trackEvent(EventType.COPY_NOTE, {
      noteType: getNotesType(notesDetails.note_type),
      noteSection: section,
      recordingId: notesDetails?.id,
    });
    navigator.clipboard.writeText(text);
    message.success("Copied to clipboard");
  }
};

export const copySessionDetails = (notesDetails: TNotes) => {
  if (notesDetails) {
    trackEvent(EventType.COPY_NOTE, {
      noteType: getNotesType(notesDetails.note_type),
      noteSection: NoteSection.SESSION_DETAILS,
      recordingId: notesDetails?.id,
    });
    let copiedText = "";

    if (notesDetails?.location_type != null || notesDetails?.duration_type != null) {
      const details = ["Session Details:"];

      if (notesDetails?.location_type != null) {
        details.push(`Location: ${notesDetails.location_type === 0 ? "Telehealth" : "In-person"}`);
      }

      if (notesDetails?.duration_type != null) {
        details.push(`CPT Code: ${durationTextFromType(notesDetails.duration_type)}\n`);
      }

      copiedText = `${details.join("\n\n")}\n\n`;
    }
    navigator.clipboard.writeText(copiedText);
    message.success("Copied to clipboard");
  }
};

export function formatTimeInLocalTimezone(dateTimeString: string): string {
  const date = new Date(dateTimeString);
  return date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit", hour12: true });
}

export const copyAddonDetails = (recordingDetails: TNotes) => {
  const psychotherapyMinutes = recordingDetails.psychotherapy_minutes || 0;
  const psychotherapyExplanation = recordingDetails.psychotherapy_minutes_explanation || "";

  const details = ["Psychotherapy Add-on:"];
  let copiedText = "";

  if (showAddon(recordingDetails)) {
    const cptCodeLabel = getCptCodeLabelByPsychotherapyMinutes(psychotherapyMinutes, recordingDetails);
    details.push(`Add-On CPT Code: ${cptCodeLabel}`);
  }

  if (psychotherapyMinutes) {
    details.push(`Psychotherapy Minutes: ${psychotherapyMinutes}`);
  }

  if (psychotherapyExplanation) {
    details.push(`Explanation: ${psychotherapyExplanation}`);
  }

  // Add psychotherapy timing information
  if (recordingDetails.stats_transcribe_start_at && psychotherapyMinutes && psychotherapyMinutes > 0) {
    const endTime = new Date(recordingDetails.stats_transcribe_start_at);
    const startTime = new Date(endTime.getTime() - psychotherapyMinutes * 60 * 1000);

    details.push(
      `In this session, psychotherapy started at ${formatTimeInLocalTimezone(startTime.toISOString())} and ended at ${formatTimeInLocalTimezone(recordingDetails.stats_transcribe_start_at)}.`,
    );
  }

  copiedText = details.join("\n\n");

  navigator.clipboard.writeText(copiedText);
  message.success("Copied to clipboard");
};

export const copyCPTDetails = (recordingDetails: TNotes) => {
  const CPTExplanation = recordingDetails.suggested_cpt_code_explanation || "";

  const details = ["Suggested CPT code:"];
  let copiedText = "";

  if (showCptCodes(recordingDetails)) {
    const cptCodeLabel = getCptSuggestedCode(recordingDetails);
    details.push(`CPT Code: ${cptCodeLabel}`);
  }

  if (CPTExplanation) {
    details.push(`Explanation: ${CPTExplanation}`);
  }

  copiedText = details.join("\n\n");

  navigator.clipboard.writeText(copiedText);
  message.success("Copied to clipboard");
};

// Helper function to check if procedural code is a psychotherapy code
const isDefaultPsychotherapyCode = (code: TProceduralCode): boolean => {
  return code.additional_fields?.psychotherapy_duration_minutes !== undefined;
};

// Helper function to get the display code for procedural codes
const getDisplayCode = (code: TProceduralCode): string => {
  if (isDefaultPsychotherapyCode(code)) {
    if (getCptCodeByPsychotherapyMinutes(code.additional_fields?.psychotherapy_duration_minutes)) {
      return getCptCodeByPsychotherapyMinutes(code.additional_fields?.psychotherapy_duration_minutes)?.toString() || "";
    }
    // This means the psychotherapy minutes is less than 16 minutes, so we use the default code
    return "90833";
  }
  return code.code;
};

// Helper function to add additional explanation for psychotherapy codes
const addAdditionalExplanation = (code: TProceduralCode, notesDetails: TNotes): string => {
  // Check if this code has psychotherapy_duration_minutes in additional_fields
  const psychotherapyMinutes = code.additional_fields?.psychotherapy_duration_minutes;

  // Add timing information if available
  if (notesDetails?.stats_transcribe_start_at && psychotherapyMinutes && psychotherapyMinutes > 0) {
    const endTime = new Date(notesDetails.stats_transcribe_start_at);
    const startTime = new Date(endTime.getTime() - psychotherapyMinutes * 60 * 1000);

    return `\n\nIn this session, psychotherapy started at ${formatTimeInLocalTimezone(startTime.toISOString())} and ended at ${formatTimeInLocalTimezone(notesDetails.stats_transcribe_start_at)}.`;
  }

  return "";
};

// The code lists without the section label, so the copy helpers and the PDF/print
// export can share one formatter while presenting it differently.
export const buildProceduralCodesBody = (notesDetails: TNotes): string => {
  if (!notesDetails?.procedural_codes || notesDetails.procedural_codes.length === 0) {
    return "";
  }

  return notesDetails.procedural_codes
    .map((code) => {
      const codeDetails = [];
      const displayCode = getDisplayCode(code);

      // Add CPT Code
      codeDetails.push(`CPT Code: ${displayCode}`);

      // Add psychotherapy minutes for psychotherapy codes OR short description for other codes
      if (isDefaultPsychotherapyCode(code)) {
        if (code.additional_fields?.psychotherapy_duration_minutes !== undefined) {
          codeDetails.push(`Psychotherapy mins: ${code.additional_fields.psychotherapy_duration_minutes}`);
        }
      }

      // Add explanation (rationale + additional timing info for psychotherapy)
      const fullExplanation = code.rationale + addAdditionalExplanation(code, notesDetails);
      codeDetails.push(`Explanation: ${fullExplanation}`);

      return codeDetails.join("\n");
    })
    .join("\n\n");
};

export const buildDiagnosticCodesBody = (notesDetails: TNotes): string => {
  if (!notesDetails?.diagnostic_codes || notesDetails.diagnostic_codes.length === 0) {
    return "";
  }

  return notesDetails.diagnostic_codes
    .map((code: TDiagnosticCode) => {
      const codeDetails = [`ICD-10 Code: ${code.code}`];

      if (code.short_description) {
        codeDetails.push(`Description: ${code.short_description}`);
      }

      const confidence = code.additional_fields?.confidence;
      if (confidence) {
        codeDetails.push(`Confidence: ${confidence}`);
      }

      if (code.rationale) {
        codeDetails.push(`Rationale: ${code.rationale}`);
      }

      return codeDetails.join("\n");
    })
    .join("\n\n");
};

// Build procedural codes text matching ProceduralCoding component formatting
export const buildProceduralCodesText = (notesDetails: TNotes, sectionLabel = "Procedural Codes"): string => {
  const codesText = buildProceduralCodesBody(notesDetails);
  return codesText ? `\n\n${sectionLabel}:\n\n${codesText}\n\n` : "";
};

export const buildDiagnosticCodesText = (notesDetails: TNotes): string => {
  const codesText = buildDiagnosticCodesBody(notesDetails);
  return codesText ? `\n\nDiagnostic Codes (ICD-10):\n\n${codesText}\n\n` : "";
};

// Medicine bills a single visit code; behavioral health can carry several.
const proceduralSectionLabelFor = (practiceArea?: number): string =>
  practiceArea === PRACTICE_AREA.MEDICINE ? "Visit Code" : "Procedural Codes";

/**
 * Whether each coding section is on screen for this note — copy and PDF/print must
 * carry exactly what the note page shows, never more.
 *
 * `has_transcript` gates the whole coding card (NotesView), and the diagnostic
 * section sits behind a further practice-area check inside the card itself.
 */
export const codingSectionVisibility = (notesDetails: TNotes, practiceArea?: number) => {
  const hasCodingCard = !!notesDetails?.has_transcript;
  return {
    showProcedural: hasCodingCard,
    showDiagnostic: hasCodingCard && isNonBehavioralHealthUser(practiceArea),
  };
};

export const durationTimeFromType = (type: string | number) => {
  switch (parseInt(type as string)) {
    case 0:
    case 13:
      return "30 min";
    case 1:
    case 14:
      return "45 minutes";
    case 2:
    case 15:
      return "60 minutes";
    case 3:
      return "15-29 minutes";
    case 4:
      return "30-44 minutes";
    case 5:
      return "45-59 minutes";
    case 6:
      return "60-74 minutes";
    case 7:
      return "10-19 minutes";
    case 8:
      return "20-29 minutes";
    case 9:
      return "30-39 minutes";
    case 10:
      return "40-54 minutes";
    case 11:
      return "45-60 minutes";
    case 12:
      return "45-60 minutes";
    case 16:
      return "60 minutes";
    case 17:
      return "45-60 minutes";
    case 18:
      return "45-60 minutes";
    case 19:
      return "45-60 minutes";
    default:
      return "-";
  }
};

export const durationTextFromType = (type: string | number): string => {
  const parsedType = parseInt(type as string);

  switch (parsedType) {
    case 0:
    case 13:
      return "90832: Psychotherapy, 30 minutes";
    case 1:
    case 14:
      return "90834: Psychotherapy, 45 minutes";
    case 2:
    case 15:
      return "90837: Psychotherapy, 60 minutes";
    case 3:
      return "99202: New patient, 15-29 minutes";
    case 4:
      return "99203: New patient, 30-44 minutes";
    case 5:
      return "99204: New Patient, 45-59 minutes";
    case 6:
      return "99205: New Patient, 60-74 minutes";
    case 7:
      return "99212: Established Patient, 10-19 minutes";
    case 8:
      return "99213: Established Patient, 20-29 minutes";
    case 9:
      return "99214: Established Patient, 30-39 minutes";
    case 10:
      return "99215: Established Patient Visit, 40-54 minutes";
    case 11:
      return "90791: Psychiatric Diagnostic Evaluation";
    case 12:
      return "90792: Psychiatric Diagnostic Evaluation Incl. Medical Services";
    case 16:
      return "90839: Psychotherapy for crisis situations, first 60 minutes";
    case 17:
      return "90846: Family Psychotherapy without patient present, 50 minutes";
    case 18:
      return "90847: Family Psychotherapy with patient present, 50 minutes";
    case 19:
      return "90853: Group Psychotherapy, not family-related";
    default:
      return "-";
  }
};

export const sortClientsByName = (clients: TClientForm[]) => {
  return clients.sort((a: any, b: any) => a.name.localeCompare(b.name));
};

export const getFirstLetterOfNameAndSurname = (name: string) => {
  if (!name) return "";
  const nameArray = name.split(" ");
  const initials = nameArray.length > 1 ? `${nameArray[0].charAt(0)}${nameArray[1].charAt(0)}` : nameArray[0].charAt(0);
  return initials.toUpperCase();
};

export function formatDistanceFromInputDateTimeToNow(dateTimeString: string) {
  return formatDistance(new Date(dateTimeString), new Date(), {
    addSuffix: true,
  });
}

export const htmlToText = (htmlContent: string): string => {
  return htmlContent
    .replace(/<br>/g, "\n") // replace <br> with newline
    .replace(/<\/p>/g, "\n") // replace </p> with newline
    .replace(/&nbsp;/g, " ") // replace &nbsp; with space
    .replace(/<[^>]*>?/gm, ""); // remove any other html tags
};

export const replaceNewLineWithBreakTag = (text: string): string => {
  return text.replace(/\n/g, "<br>");
};

export const copyContentAndNotify = (notesDetails: TNotes, htmlContent: string, plainTextContent: string) => {
  copyContent(notesDetails, htmlContent, NoteSection.ALL, plainTextContent);
};

export const generateHtmlAndPlainText = (copiedText: string, additionalText: string, addOnDetails: string) => {
  const htmlToCopy = replaceNewLineWithBreakTag(copiedText + additionalText + addOnDetails);
  const plainTextToCopy = `${copiedText}\n\n${additionalText}\n\n${addOnDetails}`;
  return { htmlToCopy, plainTextToCopy };
};

export const copyNote = (notesDetails: TNotes, copiedText: string, addOnDetails: string) => {
  switch (notesDetails.note_type) {
    case 0: // SOAP
      copiedText += `Subjective: \n${notesDetails.subject}\n\nObjective: \n${notesDetails.objective}\n\nAssessment: \n${notesDetails.assessment}\n\nPlan: \n${notesDetails.plan}`;
      if (addOnDetails) {
        copiedText += `\n\n${addOnDetails}`;
      }
      copyContentAndNotify(notesDetails, replaceNewLineWithBreakTag(copiedText), copiedText);
      break;
    case 1: // Intake
      copyContentAndNotify(
        notesDetails,
        replaceNewLineWithBreakTag(copiedText + notesDetails.intake_note + addOnDetails),
        `${copiedText}\n\n${htmlToText(notesDetails.intake_note)}${addOnDetails ? `\n\n${addOnDetails}` : ""}`,
      );
      return;
    case 2: // DAP Intake
      copiedText += `Data: \n${notesDetails.data}\n\nAssessment: \n${notesDetails.assessment}\n\nPlan: \n${notesDetails.plan}`;
      if (addOnDetails) {
        copiedText += `\n\n${addOnDetails}`;
      }
      copyContentAndNotify(notesDetails, replaceNewLineWithBreakTag(copiedText), copiedText);
      break;
    case 4: // EAP Intake
      copyContentAndNotify(
        notesDetails,
        replaceNewLineWithBreakTag(copiedText + notesDetails.eap_intake_note + addOnDetails),
        `${copiedText}\n\n${htmlToText(notesDetails.eap_intake_note)}${addOnDetails ? `\n\n${addOnDetails}` : ""}`,
      );
      return;
    case 5: // Psych Intake
      copyContentAndNotify(
        notesDetails,
        replaceNewLineWithBreakTag(copiedText + notesDetails.psych_intake_note + addOnDetails),
        `${copiedText}\n\n${htmlToText(copiedText + notesDetails.psych_intake_note)} ${addOnDetails ? `\n\n${addOnDetails}` : ""}`,
      );
      return;
    case 6: // SOL Psych Intake
      const solPsychIntake = generateHtmlAndPlainText(
        copiedText,
        notesDetails?.sol_psych_intake_note || "",
        addOnDetails,
      );
      copyContentAndNotify(notesDetails, solPsychIntake.htmlToCopy, solPsychIntake.plainTextToCopy);
      return;
    case 7: // SOL Psych Followup
      const solPsychFollowup = generateHtmlAndPlainText(
        copiedText,
        notesDetails?.sol_psych_followup_note || "",
        addOnDetails,
      );
      copyContentAndNotify(notesDetails, solPsychFollowup.htmlToCopy, solPsychFollowup.plainTextToCopy);
      return;
    case 8: // SOL Therapy Intake
      copyContentAndNotify(
        notesDetails,
        replaceNewLineWithBreakTag(copiedText + notesDetails?.sol_therapy_intake_note + addOnDetails),
        `${copiedText}\n\n${notesDetails?.sol_therapy_intake_note}${addOnDetails ? `\n\n${addOnDetails}` : ""}`,
      );
      return;
    case 9:
    case 10: // SOL Therapy Followup
      copyContentAndNotify(
        notesDetails,
        replaceNewLineWithBreakTag(copiedText + notesDetails?.sol_therapy_followup_note + addOnDetails),
        `${copiedText}\n\n${notesDetails?.sol_therapy_followup_note}${addOnDetails ? `\n\n${addOnDetails}` : ""}`,
      );
      return;
    case 11:
      const htmlToCopy = replaceNewLineWithBreakTag(copiedText + notesDetails?.psych_followup_note + addOnDetails);
      const plainTextToCopy = `${copiedText}\n\n${notesDetails?.psych_followup_note}${addOnDetails ? `\n\n${addOnDetails}` : ""}`;
      copyContent(notesDetails, htmlToCopy, NoteSection.ALL, plainTextToCopy);
      message.success("All content copied to clipboard");
      return;
    case 12:
      copyContentAndNotify(
        notesDetails,
        replaceNewLineWithBreakTag(copiedText + notesDetails.document + addOnDetails),
        `${copiedText}\n\n${htmlToText(copiedText + notesDetails.document + addOnDetails)}`,
      );
      return;
    default: // Custom Note
      copyContentAndNotify(
        notesDetails,
        replaceNewLineWithBreakTag(copiedText + notesDetails.document + addOnDetails),
        `${copiedText}\n\n${htmlToText(copiedText + notesDetails.document + addOnDetails)}}`,
      );
      return;
  }
};

/**
 * Gets the note content as plain text for use as a sample document
 */
export const getNoteContentAsText = (notesDetails: TNotes): string => {
  switch (notesDetails.note_type) {
    case NoteType.SOAP:
      return `Subjective: \n${notesDetails.subject}\n\nObjective: \n${notesDetails.objective}\n\nAssessment: \n${notesDetails.assessment}\n\nPlan: \n${notesDetails.plan}`;
    case NoteType.Intake:
      return htmlToText(notesDetails.intake_note);
    case NoteType.DAP:
      return `Data: \n${notesDetails.data}\n\nAssessment: \n${notesDetails.assessment}\n\nPlan: \n${notesDetails.plan}`;
    case NoteType.EMDR:
      return htmlToText(notesDetails.emdr_note);
    case NoteType.EAP_INTAKE:
      return htmlToText(notesDetails.eap_intake_note);
    case NoteType.PSYCHIATRIC_INTAKE:
      return htmlToText(notesDetails.psych_intake_note);
    case NoteType.SOL_PSYCH_INTAKE:
      return notesDetails?.sol_psych_intake_note || "";
    case NoteType.PSYCHIATRIC_FOLLOW_UP:
      return notesDetails?.psych_followup_note || "";
    case NoteType.THERAPY_INTAKE:
      return notesDetails?.sol_therapy_intake_note || "";
    case NoteType.THERAPY_FOLLOW_UP_DAP:
    case NoteType.THERAPY_FOLLOW_UP_SOAP:
      return notesDetails?.sol_therapy_followup_note || "";
    case NoteType.PSYCHIATRIC_FOLLOW_UP_SOAP:
      return notesDetails?.psych_followup_note || "";
    case NoteType.SOAP_SPEECH_THERAPY:
    case NoteType.CONSULT_NOTE:
    default:
      return htmlToText(notesDetails.document || "");
  }
};

/**
 * One printable block of the exported note. `body` is always plain text — the
 * newlines in it are the formatting, exactly as the clinician sees them in the
 * note editor.
 */
export type TNoteExportSection = { title: string; body: string };

const escapeHtml = (value: string): string =>
  value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

const noteFieldText = (notesDetails: TNotes, field: keyof TNotes): string =>
  ((notesDetails[field] as string | undefined) || "").toString();

/**
 * The body of the note, split into the same sections the note page shows.
 * Note types above CONSULT_NOTE are custom templates (and the imported-EHR
 * note), which carry the whole note in `document`.
 */
const buildNoteBodySections = (notesDetails: TNotes): TNoteExportSection[] => {
  switch (notesDetails.note_type) {
    case NoteType.SOAP:
      return SOAP_FIELDS.map((field) => ({
        title: field.title,
        body: noteFieldText(notesDetails, field.object as keyof TNotes),
      }));
    case NoteType.DAP:
      return DAP_FIELDS.map((field) => ({
        title: field.title,
        body: noteFieldText(notesDetails, field.object as keyof TNotes),
      }));
    case NoteType.Intake:
      return [{ title: "Intake", body: noteFieldText(notesDetails, "intake_note") }];
    case NoteType.EAP_INTAKE:
      return [{ title: "EAP Intake", body: noteFieldText(notesDetails, "eap_intake_note") }];
    case NoteType.PSYCHIATRIC_INTAKE:
      return [{ title: "Psychiatric Intake", body: noteFieldText(notesDetails, "psych_intake_note") }];
    case NoteType.EMDR:
      return [{ title: "EMDR", body: noteFieldText(notesDetails, "emdr_note") }];
    case NoteType.SOL_PSYCH_INTAKE:
      return [{ title: "Psychiatric Intake", body: noteFieldText(notesDetails, "sol_psych_intake_note") }];
    case NoteType.PSYCHIATRIC_FOLLOW_UP:
      return [{ title: "Psychiatric Followup", body: noteFieldText(notesDetails, "psych_followup_note") }];
    case NoteType.THERAPY_INTAKE:
      return [{ title: "Therapy Intake", body: noteFieldText(notesDetails, "sol_therapy_intake_note") }];
    case NoteType.THERAPY_FOLLOW_UP_DAP:
    case NoteType.THERAPY_FOLLOW_UP_SOAP:
      return [{ title: "Therapy Followup", body: noteFieldText(notesDetails, "sol_therapy_followup_note") }];
    case NoteType.PSYCHIATRIC_FOLLOW_UP_SOAP:
      return [{ title: "Psychiatric Followup (SOAP)", body: noteFieldText(notesDetails, "psych_followup_note") }];
    case NoteType.SOAP_SPEECH_THERAPY:
      return [{ title: "SOAP (Speech Therapy)", body: noteFieldText(notesDetails, "document") }];
    case NoteType.CONSULT_NOTE:
      return [{ title: "Consult Note", body: noteFieldText(notesDetails, "document") }];
    default:
      return [{ title: notesDetails?.note_type_info?.name || "Note", body: noteFieldText(notesDetails, "document") }];
  }
};

const buildSessionDetailsSection = (notesDetails: TNotes): TNoteExportSection | null => {
  const details: string[] = [];

  if (notesDetails.location_type != null) {
    details.push(`Location: ${notesDetails.location_type === 0 ? "Telehealth" : "In-person"}`);
  }
  if (notesDetails.duration_type != null) {
    details.push(`CPT Code: ${durationTextFromType(notesDetails.duration_type)}`);
  }

  return details.length ? { title: "Session Details", body: details.join("\n") } : null;
};

/**
 * Everything the Print / Download PDF output contains, in order: session details,
 * the note itself, then the coding sections.
 *
 * The export must show what the note page shows and nothing else, so each section
 * repeats that page's visibility rule — `has_transcript` gates the coding card
 * (NotesView), and diagnostic codes are additionally behind the same
 * `isNonBehavioralHealthUser` check the card itself applies.
 */
export const buildNoteExportSections = (notesDetails: TNotes, practiceArea?: number): TNoteExportSection[] => {
  const sections: TNoteExportSection[] = [];

  const sessionDetails = buildSessionDetailsSection(notesDetails);
  if (sessionDetails) {
    sections.push(sessionDetails);
  }

  sections.push(...buildNoteBodySections(notesDetails).filter((section) => section.body.trim()));

  const { showProcedural, showDiagnostic } = codingSectionVisibility(notesDetails, practiceArea);

  const proceduralCodes = showProcedural ? buildProceduralCodesBody(notesDetails) : "";
  if (proceduralCodes) {
    sections.push({ title: proceduralSectionLabelFor(practiceArea), body: proceduralCodes });
  }

  const diagnosticCodes = showDiagnostic ? buildDiagnosticCodesBody(notesDetails) : "";
  if (diagnosticCodes) {
    sections.push({ title: "Diagnostic Codes (ICD-10)", body: diagnosticCodes });
  }

  return sections;
};

const buildNoteExportHeader = (notesDetails: TNotes): { title: string; meta: string } => {
  const meta: string[] = [];

  if (notesDetails.client_name) {
    meta.push(notesDetails.client_name);
  }
  if (notesDetails.uploaded_at) {
    meta.push(dayjs(notesDetails.uploaded_at).format("MMM D, YYYY [at] h:mm A"));
  }

  return { title: notesDetails.title || "Untitled Session", meta: meta.join("  ·  ") };
};

export const generateHTMLForPDF = (notesDetails: TNotes, practiceArea?: number): string => {
  const { title, meta } = buildNoteExportHeader(notesDetails);

  const sections = buildNoteExportSections(notesDetails, practiceArea)
    .map(
      (section) =>
        `<section class="pdf-section">` +
        `<h2 class="pdf-section__title">${escapeHtml(section.title)}</h2>` +
        `<div class="pdf-section__body">${escapeHtml(section.body)}</div>` +
        `</section>`,
    )
    .join("");

  return `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <title>${escapeHtml(title)}</title>
    <style>
      @page { margin: 0.6in; }
      body {
        font-family: Arial, Helvetica, sans-serif;
        font-size: 11pt;
        line-height: 1.5;
        color: #000;
        margin: 0;
        padding: 0;
      }
      .pdf-header {
        border-bottom: 1px solid #999;
        margin-bottom: 20px;
        padding-bottom: 8px;
      }
      .pdf-header__title { font-size: 16pt; margin: 0 0 4px; }
      .pdf-header__meta { font-size: 10pt; color: #444; }
      .pdf-section { margin-bottom: 20px; }
      .pdf-section__title {
        font-size: 12pt;
        margin: 0 0 6px;
        /* Never leave a heading stranded at the foot of a page. */
        break-after: avoid;
        page-break-after: avoid;
      }
      .pdf-section__body {
        /* The note is stored and edited as plain text, so its newlines and
           indentation ARE the formatting — preserve them verbatim rather than
           letting HTML collapse the whole note into one paragraph. */
        white-space: pre-wrap;
        overflow-wrap: break-word;
      }
    </style>
  </head>
  <body>
    <header class="pdf-header">
      <h1 class="pdf-header__title">${escapeHtml(title)}</h1>
      ${meta ? `<div class="pdf-header__meta">${escapeHtml(meta)}</div>` : ""}
    </header>
    ${sections}
  </body>
</html>`;
};

export const copyAllContent = (notesDetails: TNotes, practiceArea?: number) => {
  if (!notesDetails) return;

  let copiedText = "";
  let addOnDetails = "";

  trackEvent(EventType.COPY_NOTE, {
    noteType: getNotesType(notesDetails.note_type),
    noteSection: NoteSection.ALL,
    recordingId: notesDetails?.id,
  });

  const addDetails = (detailsArray: string[]) => "\n\n" + detailsArray.join("\n\n") + "\n\n";

  if (notesDetails.location_type != null || notesDetails.duration_type != null) {
    const details = ["Session Details:"];
    if (notesDetails.location_type != null) {
      details.push(`Location: ${notesDetails.location_type === 0 ? "Telehealth" : "In-person"}`);
    }
    if (notesDetails.duration_type != null) {
      details.push(`CPT Code: ${durationTextFromType(notesDetails.duration_type)}`);
    }
    copiedText = addDetails(details);
  }

  // Copy only the coding sections the note page is actually showing.
  const { showProcedural, showDiagnostic } = codingSectionVisibility(notesDetails, practiceArea);

  const proceduralCodesText = showProcedural
    ? buildProceduralCodesText(notesDetails, proceduralSectionLabelFor(practiceArea))
    : "";
  const diagnosticCodesText = showDiagnostic ? buildDiagnosticCodesText(notesDetails) : "";

  if (proceduralCodesText) {
    addOnDetails += proceduralCodesText;
  }

  if (diagnosticCodesText) {
    addOnDetails += diagnosticCodesText;
  }

  copyNote(notesDetails, copiedText, addOnDetails);
  message.success("All content copied to clipboard");
};

/** Keep the downloaded filename recognisable but safe on every OS. */
const pdfFileNameFor = (notesDetails: TNotes, title: string): string => {
  const slug = title
    .replace(/[^\w\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .slice(0, 80);
  return slug ? `${slug}.pdf` : `note-${notesDetails.id}.pdf`;
};

export const downloadNotePDF = (notesDetails: TNotes, practiceArea?: number) => {
  if (!notesDetails) return;

  const { title, meta } = buildNoteExportHeader(notesDetails);

  // Built straight from the section data — the old code round-tripped through
  // the print HTML and stripped tags, which dropped every line break and left
  // raw entities (&amp;) in the text.
  const content: Content[] = [{ text: title, style: "documentTitle" }];
  if (meta) {
    content.push({ text: meta, style: "documentMeta" });
  }

  for (const section of buildNoteExportSections(notesDetails, practiceArea)) {
    content.push({
      stack: [
        { text: section.title, style: "sectionTitle" },
        { text: section.body, style: "sectionBody", preserveLeadingSpaces: true },
      ],
      margin: [0, 0, 0, 14] as [number, number, number, number],
    });
  }

  const docDefinition: TDocumentDefinitions = {
    info: { title },
    content,
    styles: {
      documentTitle: { fontSize: 16, bold: true, margin: [0, 0, 0, 2] },
      documentMeta: { fontSize: 9, color: "#444444", margin: [0, 0, 0, 14] },
      sectionTitle: { fontSize: 12, bold: true, margin: [0, 0, 0, 4] },
      sectionBody: { fontSize: 11, lineHeight: 1.4 },
    },
    defaultStyle: {
      fontSize: 11,
      lineHeight: 1.4,
      font: "Roboto",
    },
    pageMargins: [40, 45, 40, 45],
  };

  pdfMake.createPdf(docDefinition).download(pdfFileNameFor(notesDetails, title));
};
